import {ApiClient, type ErrorResponse} from '@hconnect/apiclient'
import {useUpmPlantContext} from '@hconnect/upm'
import {spawnErrorToast, spawnSuccessToast} from '@hm/fundamental'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useCallback} from 'react'
import {useErrorHandler} from 'react-error-boundary'
import {useTranslation} from 'react-i18next'

import {
  FEATURE_NAMESPACE,
  OPTIMIZER_RUNS_AND_SCHEDULES_QUERY_KEY,
  getOptimizerRunPath
} from '../consts'
import {notifyIfErrorMessage} from '../utils'

type Variables = {runId: number}

export const useCancelOptimizerRun = () => {
  const {plantId: plantCode} = useUpmPlantContext()
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const raiseError = useErrorHandler()
  const queryClient = useQueryClient()

  const {mutate, isPending: isLoading} = useMutation<void, AxiosError<ErrorResponse>, Variables>({
    mutationFn: async ({runId}) => {
      await ApiClient.axiosInstance.delete<void>(getOptimizerRunPath(plantCode, runId))
    },
    onError: (error) => {
      spawnErrorToast(t('error.apology'))
      notifyIfErrorMessage(error.response?.data.detail, raiseError, spawnErrorToast)
    },
    onSuccess: () => {
      spawnSuccessToast(t('success.cancelOptimizerRun'))
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: [OPTIMIZER_RUNS_AND_SCHEDULES_QUERY_KEY, {plantCode}]
      })
    }
  })

  const cancelOptimizerRun = useCallback((runId: number) => mutate({runId}), [mutate])

  return {cancelOptimizerRun, isLoading}
}
